/*
 * useAuftragEditGui.tsx - Auftrag bearbeiten.
 *
 * Created by Dr. Maximillian Dornseif 2023-11-10 in huWaWi3 33.7.0
 * Copyright (c) 2023 HUDORA GmbH
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useSchema } from '@hudora/hd-react-components'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { dateTimeifyTyped } from 'any-to-luxon'
import { assertIsNumber } from 'assertate-debug'
import gql from 'graphql-tag'
import { JSONSchema7 } from 'json-schema'
import * as React from 'react'
import { ResolverResult, useForm } from 'react-hook-form'
import * as z from 'zod'

import { ICommandBarItemProps } from '../../lib/muipanels'
import { AUFTRAG_DATE_MUTATION } from '../Auftraege/useZuteilungGui'
import { useHdDialog } from '../components/HdDialog'
import { useEditDialog } from '../components/useEditDialog'
import { useToastMutation } from '../components/useToastMutation'
import { ZEITFENSTER_DEFAULT, ZEITFENSTER_MAX, ZEITFENSTER_MIN } from '../Entwuerfe/WaWiEntwurfErfassen'
import { AnlieferdatumInput3 } from '../Reservierungen/AnlieferdatumInput3'
import { AUFTRAG_FLOAT_MUTATION, AUFTRAG_TEXT_MUTATION } from './WaWiAuftragEinerQuery'

const schema = z.object({
  anlieferdatum_von: z.any(),
  zeitfenster: z.number().min(ZEITFENSTER_MIN).max(ZEITFENSTER_MAX),
})
const zresolver = zodResolver(schema)

export function useAuftragEditGui(auftrag) {
  const submitFormRef = React.useRef(null)
  const { schema } = useSchema('HwAuftrag')
  const [editAuftragsnrKunde, dialogAuftragsnrKunde] = useEditDialog({
    subSchema: schema?.properties?.auftragsnr_kunde as JSONSchema7,
    propertyName: 'auftragsnr_kunde',
    variables: { key: auftrag._keyStr, oldRev: auftrag._rev },
    mutation: AUFTRAG_TEXT_MUTATION,
  })
  const [editInfotextKunde, dialogInfotextKunde] = useEditDialog({
    subSchema: schema?.properties?.infotext_kunde as JSONSchema7,
    propertyName: 'infotext_kunde',
    variables: { key: auftrag._keyStr, oldRev: auftrag._rev },
    mutation: AUFTRAG_TEXT_MUTATION,
  })
  const [editInfotextIntern, dialogInfotextIntern] = useEditDialog({
    subSchema: schema?.properties?.infotext_intern as JSONSchema7,
    propertyName: 'infotext_intern',
    variables: { key: auftrag._keyStr, oldRev: auftrag._rev },
    mutation: AUFTRAG_TEXT_MUTATION,
  })
  const [editAuftragsrabattProzent, dialogAuftragsrabattProzent] = useEditDialog({
    subSchema: schema?.properties?.auftragsrabatt_prozent as JSONSchema7,
    propertyName: 'auftragsrabatt_prozent',
    variables: { key: auftrag._keyStr, oldRev: auftrag._rev },
    mutation: AUFTRAG_FLOAT_MUTATION,
  })
  const [editAuftragsrabattText, dialogAuftragsrabattText] = useEditDialog({
    subSchema: schema?.properties?.auftragsrabatt_text as JSONSchema7,
    propertyName: 'auftragsrabatt_text',
    variables: { key: auftrag._keyStr, oldRev: auftrag._rev },
    mutation: AUFTRAG_TEXT_MUTATION,
  })
  const {
    openDialog: openAnlieferDialog,
    renderDialog: renderAnlieferDialog,
    dismissDialog: dismissAnlieferDialog,
    isDialogOpen: isAnlieferDialogOpen,
  } = useHdDialog(`Anlieferdatum bearbeiten`, 'Daten im bestehenden Auftrag verändern.')

  const auftragErledigt = auftrag.status >= '600'

  const item: ICommandBarItemProps = {
    key: 'textBearbeiten',
    text: 'Bearbeiten',
    ariaLabel: 'Bearbeiten',
    iconOnly: false,
    iconProps: { iconName: auftrag.status >= '100ready' ? 'Edit' : 'Uneditable' },
    disabled: !(auftrag.status >= '100ready'),
    subMenuProps: {
      items: [
        {
          key: 'Kundenreferenz',
          text: 'Kundenreferenz',
          iconProps: { iconName: 'NumberSymbol' },
          disabled: auftragErledigt,
          onClick: () => editAuftragsnrKunde(auftrag.auftragsnr_kunde),
        },
        {
          key: 'infotextKunde',
          text: 'Kunden-Info',
          iconProps: { iconName: 'ChatInviteFriend' },
          disabled: auftragErledigt,
          onClick: () => editInfotextKunde(auftrag.infotext_kunde),
        },
        {
          key: 'infotextIntern',
          text: 'interne Info',
          iconProps: { iconName: 'MuteChat' },
          disabled: auftragErledigt,
          onClick: () => editInfotextIntern(auftrag.infotext_intern),
        },
        {
          key: 'anlieferdatum',
          text: 'Anlieferdatum',
          iconProps: { iconName: 'DateTime' },
          disabled: auftragErledigt,
          onClick: () => openAnlieferDialog(),
        },
        // {
        //   key: 'infotextLogistik',
        //   text: 'logistik Info',
        //   iconProps: { iconName: 'Chat' },
        //   disabled: auftragErledigt,
        //   onClick: () => edit('infotext_logistik'),
        // },
        {
          key: 'auftagsrabatt',
          text: 'Auftragsrabatt',
          iconProps: { iconName: 'Money' },
          disabled: false && !(auftrag.status < '301'),
          onClick: () => editAuftragsrabattProzent(auftrag.auftragsrabatt_prozent),
        },
        {
          key: 'auftagsrabattText',
          text: 'Auftragsrabatt Beschreibung',
          iconProps: { iconName: 'EditMirrored' },
          disabled: false && !(auftrag.status < '301'),
          onClick: () => editAuftragsrabattText(auftrag.auftragsrabatt_text),
        },
      ],
    },
  }

  const anlieferDialog = (
    <>
      {renderAnlieferDialog({
        renderBody: () =>
          auftrag ? <AnlieferdatumEditor auftrag={auftrag} submitFormRef={submitFormRef} /> : null,
        renderActions: () => (
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button variant="outlined" onClick={dismissAnlieferDialog}>
              Abbrechen
            </Button>
            <Button variant="contained" onClick={() => (submitFormRef?.current as any)?.click()}>
              Speichern
            </Button>
          </Stack>
        ),
      })}
    </>
  )

  const dialog = (
    <React.Fragment>
      {dialogAuftragsnrKunde}
      {dialogInfotextKunde}
      {dialogInfotextIntern}
      {anlieferDialog}
      {dialogAuftragsrabattProzent}
      {dialogAuftragsrabattText}
    </React.Fragment>
  )
  return [item, dialog]
}
function AnlieferdatumEditor({
  auftrag,
  submitFormRef,
}): React.ReactElement<any, string | React.JSXElementConstructor<any>> {
  const anlieferdatumVon = dateTimeifyTyped(auftrag.anlieferdatum_von)
  const anlieferdatumBis = dateTimeifyTyped(auftrag.anlieferdatum_bis).toUTC()
  const zeitfenster = anlieferdatumBis.diff(anlieferdatumVon).as('days')
  assertIsNumber(zeitfenster)
  console.log(zeitfenster)

  const [, mutateAnlieferdatum] = useToastMutation({
    mutation: AUFTRAG_DATE_MUTATION,
    variables: { key: auftrag._keyStr, oldRev: auftrag._rev },
    fieldTitle: 'Anlieferdatum',
  })
  const { handleSubmit, control } = useForm({
    mode: 'all',
    delayError: 250,
    shouldFocusError: true,
    defaultValues: { anlieferdatum_von: anlieferdatumVon.toUTC().toISO(), zeitfenster },
    resolver: async (values: any, context: object, options): Promise<ResolverResult> => {
      return await zresolver(values, context, options)
    },
  })

  return (
    <div>
      <form
        onSubmit={handleSubmit(
          async (data: Record<string, any>, e?: Partial<Event>) => {
            const anlieferdatum_von = dateTimeifyTyped(data.anlieferdatum_von).toUTC().toISO()
            const anlieferdatum_bis = dateTimeifyTyped(anlieferdatum_von)
              .plus({ days: data.zeitfenster })
              .toUTC()
              .toISO()
            console.log({ ...data, anlieferdatum_von, anlieferdatum_bis })
            const { data: mut1data } = await mutateAnlieferdatum({
              value: anlieferdatum_von,
              fieldName: 'anlieferdatum_von',
              message: 'Anlieferdatum von verschoben',
              fieldTitle: 'Anlieferdatum von',
            })
            console.log(mut1data)
            await mutateAnlieferdatum({
              value: anlieferdatum_bis,
              fieldName: 'anlieferdatum_bis',
              message: 'Anlieferdatum bis verschoben',
              fieldTitle: 'Anlieferdatum bis',
              oldRev: mut1data.result.auftrag._rev,
            })
          },
          (errors: Object, e?: Partial<Event>) => console.log(errors)
        )}
      >
        <AnlieferdatumInput3
          control={control}
          zeitfensterMin={ZEITFENSTER_MIN}
          zeitfensterMax={ZEITFENSTER_MAX}
          zeitfensterDefault={ZEITFENSTER_DEFAULT}
        />
        <button ref={submitFormRef} type="submit" style={{ display: 'none' }} />
      </form>
    </div>
  )
}
